import React, { useState, useEffect, useRef } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { Star, Shield, MapPin, Phone, MessageSquare, Heart, Lock, Camera, X, CalendarPlus, Crown, Home, Clock, UserCircle, Send, CheckCircle, Loader2, Instagram, Facebook, Globe, Pencil, Settings, ChevronDown, ChevronUp } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { toast } from 'sonner';
import api, { API_BASE } from '@/lib/api';
import BookingForm from '@/components/BookingForm';
import AmenitiesDisplay from '@/components/AmenitiesDisplay';
import AdminEditModal from '@/components/AdminEditModal';

const GOOGLE_MAPS_KEY = process.env.REACT_APP_GOOGLE_MAPS_KEY || '';

const SafeMap = ({ latitude, longitude, address, municipio }) => {
  const lat = parseFloat(latitude);
  const lng = parseFloat(longitude);
  if (!lat || !lng) {
    if (!address && !municipio) return null;
    return (
      <div className="h-[250px] bg-gray-100 rounded-xl flex items-center justify-center text-gray-500 text-base">
        <div className="text-center">
          <MapPin className="w-8 h-8 mx-auto mb-2 text-gray-400" />
          <p>Ubicación no disponible</p>
        </div>
      </div>
    );
  }
  const zoom = 16;
  const src = `https://maps.google.com/maps?q=${lat},${lng}&z=${zoom}&output=embed`;
  return (
    <iframe
      title="Ubicación"
      className="w-full h-[250px] rounded-xl"
      style={{ border: 0 }}
      loading="lazy"
      src={src}
    />
  );
};

// Criterios de evaluación
const REVIEW_CRITERIA = [
  { id: 'personal', label: 'Calidad del servicio' },
  { id: 'instalaciones', label: 'Atención y trato' },
  { id: 'visitas', label: 'Instalaciones y ambiente' },
  { id: 'comida', label: 'Relación precio - calidad' },
  { id: 'actividades', label: 'Experiencia general' }
];

// Componente de estrellas para cada criterio
const StarRating = ({ value, onChange, size = 'md' }) => {
  const sizeClass = size === 'lg' ? 'w-8 h-8' : 'w-6 h-6';
  return (
    <div className="flex gap-1">
      {[1, 2, 3, 4, 5].map((star) => (
        <button
          key={star}
          type="button"
          onClick={() => onChange(star)}
          className="focus:outline-none transition-transform hover:scale-110"
        >
          <Star
            className={`${sizeClass} ${star <= value ? 'fill-yellow-400 text-yellow-400' : 'text-gray-300'}`}
          />
        </button>
      ))}
    </div>
  );
};

const API_URL = process.env.REACT_APP_BACKEND_URL;

const getPhotoUrl = (path) => {
  if (!path) return null;
  if (path.startsWith('http')) return path;
  return `${API_URL}${path}`;
};

const extractYouTubeId = (url) => {
  if (!url) return '';
  const match = url.match(/(?:youtu\.be\/|youtube\.com\/(?:embed\/|v\/|watch\?v=|shorts\/))([^?&\s]+)/);
  return match ? match[1] : '';
};

// Premium Slider Component - Booking.com style grid
const PremiumSlider = ({ photos }) => {
  const [lightboxOpen, setLightboxOpen] = useState(false);
  const [lightboxIndex, setLightboxIndex] = useState(0);

  if (!photos || photos.length === 0) return null;

  const openLightbox = (index) => {
    setLightboxIndex(index);
    setLightboxOpen(true);
  };

  const closeLightbox = () => setLightboxOpen(false);
  const goPrev = () => setLightboxIndex((lightboxIndex - 1 + photos.length) % photos.length);
  const goNext = () => setLightboxIndex((lightboxIndex + 1) % photos.length);

  const MAX_THUMBS = 5;
  const thumbPhotos = photos.slice(0, MAX_THUMBS);
  const extraCount = photos.length - MAX_THUMBS;

  return (
    <>
      <div className="relative w-full rounded-2xl overflow-hidden shadow-lg" data-testid="premium-slider">
        {/* Premium badge */}
        <div className="absolute top-4 left-4 z-10 flex items-center gap-1.5 bg-gradient-to-r from-yellow-400 to-yellow-500 text-[#000000] text-xs font-bold px-3 py-1.5 rounded-full" data-testid="premium-badge">
          <Crown className="w-3.5 h-3.5" />
          Premium
        </div>

        {/* Main grid: 1 large left + 2 stacked right */}
        <div className="flex gap-1" style={{ height: '400px' }}>
          {/* Large photo left */}
          <div className="flex-[3] relative cursor-pointer overflow-hidden" onClick={() => openLightbox(0)}>
            <img
              src={getPhotoUrl(photos[0]?.url)}
              alt="Foto principal"
              className="w-full h-full object-cover hover:scale-105 transition-transform duration-300"
            />
          </div>

          {/* 2 stacked photos right */}
          {photos.length > 1 && (
            <div className="flex-[2] flex flex-col gap-1">
              <div className="flex-1 relative cursor-pointer overflow-hidden" onClick={() => openLightbox(1)}>
                <img
                  src={getPhotoUrl(photos[1]?.url)}
                  alt="Foto 2"
                  className="w-full h-full object-cover hover:scale-105 transition-transform duration-300"
                />
              </div>
              {photos.length > 2 && (
                <div className="flex-1 relative cursor-pointer overflow-hidden" onClick={() => openLightbox(2)}>
                  <img
                    src={getPhotoUrl(photos[2]?.url)}
                    alt="Foto 3"
                    className="w-full h-full object-cover hover:scale-105 transition-transform duration-300"
                  />
                </div>
              )}
            </div>
          )}
        </div>

        {/* Thumbnails row */}
        {photos.length > 3 && (
          <div className="flex gap-1 mt-1">
            {thumbPhotos.map((photo, i) => (
              <div
                key={i}
                className="relative flex-1 h-20 cursor-pointer overflow-hidden"
                onClick={() => openLightbox(i)}
              >
                <img
                  src={getPhotoUrl(photo.url)}
                  alt={`Miniatura ${i + 1}`}
                  className="w-full h-full object-cover hover:brightness-75 transition-all"
                />
                {/* "X fotos más" on last thumbnail */}
                {i === MAX_THUMBS - 1 && extraCount > 0 && (
                  <div className="absolute inset-0 bg-black/60 flex items-center justify-center">
                    <span className="text-white text-sm font-bold">+{extraCount} fotos</span>
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Lightbox */}
      {lightboxOpen && (
        <div className="fixed inset-0 z-50 bg-black/90 flex items-center justify-center" onClick={closeLightbox}>
          <div className="relative max-w-5xl max-h-[90vh] w-full mx-4" onClick={(e) => e.stopPropagation()}>
            <img
              src={getPhotoUrl(photos[lightboxIndex]?.url)}
              alt={`Foto ${lightboxIndex + 1}`}
              className="w-full max-h-[85vh] object-contain rounded-lg"
            />
            {/* Close */}
            <button
              onClick={closeLightbox}
              className="absolute -top-12 right-0 text-white hover:text-gray-300 transition-colors"
              data-testid="lightbox-close"
            >
              <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" /></svg>
            </button>
            {/* Prev */}
            {photos.length > 1 && (
              <button
                onClick={goPrev}
                className="absolute left-2 top-1/2 -translate-y-1/2 w-12 h-12 rounded-full bg-white/20 backdrop-blur-sm flex items-center justify-center hover:bg-white/40 transition-colors"
                data-testid="lightbox-prev"
              >
                <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" /></svg>
              </button>
            )}
            {/* Next */}
            {photos.length > 1 && (
              <button
                onClick={goNext}
                className="absolute right-2 top-1/2 -translate-y-1/2 w-12 h-12 rounded-full bg-white/20 backdrop-blur-sm flex items-center justify-center hover:bg-white/40 transition-colors"
                data-testid="lightbox-next"
              >
                <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" /></svg>
              </button>
            )}
            {/* Counter */}
            <div className="absolute bottom-4 left-1/2 -translate-x-1/2 bg-black/50 text-white text-sm px-4 py-1.5 rounded-full">
              {lightboxIndex + 1} / {photos.length}
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default function ProviderProfile() {
  const { providerId } = useParams();
  const [provider, setProvider] = useState(null);
  const [loading, setLoading] = useState(true);
  const [user, setUser] = useState(null);
  const [reviewText, setReviewText] = useState('');
  // Estado para los 5 criterios de evaluación (0 = no seleccionado)
  const [reviewCriteria, setReviewCriteria] = useState({
    personal: 0,
    instalaciones: 0,
    visitas: 0,
    comida: 0,
    actividades: 0
  });
  const [reviewPhotos, setReviewPhotos] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [showBookingForm, setShowBookingForm] = useState(false);
  const [sendingContactRequest, setSendingContactRequest] = useState(false);
  const [contactMessage, setContactMessage] = useState('');
  const [contactSent, setContactSent] = useState(false);
  const fileInputRef = useRef(null);
  const [editSection, setEditSection] = useState(null);
  const [isFavorite, setIsFavorite] = useState(false);
  const [togglingFav, setTogglingFav] = useState(false);
  const [expandedServices, setExpandedServices] = useState({});
  const [showOwnerContact, setShowOwnerContact] = useState(false);
  const [ownerForm, setOwnerForm] = useState({ nombre: '', email: '', mensaje: '' });
  const [sendingOwnerForm, setSendingOwnerForm] = useState(false);
  const isAdmin = user?.role === 'admin';
  const isClient = user && user.role !== 'admin' && user.role !== 'provider';

  // Calcular promedio de los criterios
  const calculateAverageRating = () => {
    const values = Object.values(reviewCriteria);
    const filledValues = values.filter(v => v > 0);
    if (filledValues.length === 0) return 0;
    return filledValues.reduce((a, b) => a + b, 0) / filledValues.length;
  };

  // Verificar si todos los criterios están llenos
  const allCriteriaFilled = () => {
    return Object.values(reviewCriteria).every(v => v > 0);
  };

  // Verificar si el formulario está completo
  const canSubmitReview = () => {
    return allCriteriaFilled() && reviewText.trim().length > 0;
  };

  useEffect(() => {
    loadProvider();
    loadUser();
  }, [providerId]);

  const loadProvider = async () => {
    try {
      const res = await api.get(`/providers/${providerId}`);
      setProvider(res.data);
    } catch (e) {
      toast.error('Cuidador no encontrado');
    } finally {
      setLoading(false);
    }
  };

  const loadUser = async () => {
    try {
      const res = await api.get('/auth/me');
      setUser(res.data);
    } catch {}
  };

  // Check favorite status when user and provider are loaded
  useEffect(() => {
    if (user && providerId && user.role !== 'admin' && user.role !== 'provider') {
      api.get(`/favorites/check/${providerId}`)
        .then(res => setIsFavorite(res.data.is_favorite))
        .catch(() => {});
    }
  }, [user, providerId]);

  const toggleFavorite = async () => {
    if (!user) return;
    setTogglingFav(true);
    try {
      if (isFavorite) {
        await api.delete(`/favorites/${providerId}`);
        setIsFavorite(false);
        toast.success('Eliminado de favoritos');
      } else {
        await api.post(`/favorites/${providerId}`);
        setIsFavorite(true);
        toast.success('Añadido a favoritos');
      }
    } catch {
      toast.error('Error al actualizar favoritos');
    } finally {
      setTogglingFav(false);
    }
  };

  const handlePhotoUpload = async (e) => {
    const files = Array.from(e.target.files || []);

    if (!files.length) return;

    if (reviewPhotos.length + files.length > 4) {
      toast.error('Máximo 4 fotos');
      e.target.value = '';
      return;
    }

    setUploading(true);

    for (const file of files) {
      const preview = URL.createObjectURL(file);

      try {
        const formData = new FormData();
        formData.append('file', file);

        const res = await api.post('/reviews/upload-photo', formData, {
          headers: { 'Content-Type': 'multipart/form-data' }
        });

        const uploadedUrl =
          res.data?.url ||
          res.data?.photo_url ||
          res.data?.image_url ||
          res.data?.path;

        if (!uploadedUrl) {
          URL.revokeObjectURL(preview);
          throw new Error('El servidor no devolvió la URL de la imagen');
        }

        setReviewPhotos(prev => [
          ...prev,
          {
            url: uploadedUrl,
            preview
          }
        ]);
      } catch (error) {
        URL.revokeObjectURL(preview);
        toast.error(
          error.response?.data?.detail ||
          error.message ||
          'Error al subir foto'
        );
      }
    }

    setUploading(false);
    e.target.value = '';
  };

  const submitReview = async () => {
    if (!allCriteriaFilled()) { 
      toast.error('Debes evaluar todos los criterios'); 
      return; 
    }
    if (!reviewText.trim()) { 
      toast.error('Debes escribir un comentario'); 
      return; 
    }
    
    setSubmitting(true);
    const averageRating = calculateAverageRating();
    
    try {
      await api.post('/reviews', {
        provider_id: providerId,
        rating: Math.round(averageRating * 10) / 10, // Promedio con 1 decimal
        criteria: reviewCriteria, // Enviar los 5 criterios individuales
        comment: reviewText,
        photos: reviewPhotos.map(photo => typeof photo === 'string' ? photo : photo.url)
      });
      toast.success('¡Gracias por tu reseña! Se publicará cuando sea aprobada.');
      setReviewText('');
      setReviewCriteria({
        personal: 0,
        instalaciones: 0,
        visitas: 0,
        comida: 0,
        actividades: 0
      });
      setReviewPhotos([]);
      loadProvider();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Error al enviar reseña');
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) return <div className="min-h-screen flex items-center justify-center"><div className="animate-spin w-16 h-16 border-4 border-[#ffff00] border-t-transparent rounded-full" /></div>;
  if (!provider) return <div className="min-h-screen flex items-center justify-center text-gray-500">Cuidador no encontrado</div>;

  const EditBtn = ({ section, label }) => isAdmin ? (
    <button
      onClick={() => setEditSection(section)}
      className="inline-flex items-center gap-1 px-2 py-1 text-xs font-medium text-white bg-[#000000] hover:bg-[#ffff00] hover:text-[#000000] rounded-full transition-colors ml-2"
      data-testid={`admin-edit-${section}`}
    >
      <Pencil className="w-3 h-3" />
      {label || 'Editar'}
    </button>
  ) : null;

  return (
    <div className="min-h-screen bg-gray-50" data-testid="provider-profile">
      {/* Admin Edit Modal */}
      {editSection && (
        <AdminEditModal
          section={editSection}
          provider={provider}
          onClose={() => setEditSection(null)}
          onSaved={loadProvider}
        />
      )}

      {/* Admin Floating Bar */}
      {isAdmin && (
        <div className="sticky top-0 z-40 bg-[#000000] text-white px-4 py-2 flex items-center justify-between shadow-lg" data-testid="admin-edit-bar">
          <div className="flex items-center gap-2">
            <Settings className="w-4 h-4" />
            <span className="text-sm font-medium">Modo Admin</span>
            <span className="text-xs text-white/60">- Haz clic en los botones "Editar" de cada sección</span>
          </div>
          <div className="flex gap-2">
            <button
              onClick={() => setEditSection('settings')}
              className="px-3 py-1 text-xs font-bold bg-[#ffff00] text-[#000000] rounded-full hover:bg-white transition-colors"
              data-testid="admin-settings-btn"
            >
              Destacado / Plan / Place ID
            </button>
          </div>
        </div>
      )}
      {/* Hero */}
      <div className="relative bg-[#ffff00] pt-6 pb-6 md:h-64 md:pt-0 md:pb-0">
        <div className="md:absolute md:inset-0 md:flex md:items-end">
          <div className="max-w-5xl mx-auto w-full px-4 md:pb-8">
            {/* Mobile layout */}
            <div className="md:hidden">
              <div className="flex items-start justify-between mb-1">
                <div className="flex items-end gap-3">
                  <div className="w-24 h-24 rounded-2xl bg-white shadow-xl flex items-center justify-center overflow-hidden shrink-0">
                    {provider.profile_photo ? (
                      <img src={getPhotoUrl(provider.profile_photo)} alt="" className="w-full h-full object-cover" />
                    ) : provider.gallery?.[0]?.url ? (
                      <img src={getPhotoUrl(provider.gallery[0].url)} alt="" className="w-full h-full object-cover" />
                    ) : provider.photos?.[0] ? (
                      <img src={provider.photos[0]} alt="" className="w-full h-full object-cover" />
                    ) : (
                      <span className="text-3xl font-bold text-[#ffff00]">{provider.business_name?.[0]}</span>
                    )}
                  </div>
                  <div className="flex items-center gap-1 pb-0.5">
                    {[1,2,3,4,5].map(s => (
                      <Star key={s} className={`w-4 h-4 ${s <= Math.round(provider.rating || 0) ? 'fill-yellow-400 text-yellow-400' : 'text-gray-300'}`} />
                    ))}
                    <span className="text-sm font-bold text-[#000000] ml-1" data-testid="provider-rating">{provider.rating?.toFixed(1) || '0.0'}</span>
                    <span className="text-xs text-[#000000]/60">({provider.total_reviews || 0})</span>
                  </div>
                </div>
                <div className="flex flex-col items-end gap-2">
                  {provider.plan_type === 'premium_plus' && (
                    <span className="bg-gradient-to-r from-yellow-400 to-yellow-500 text-[#000000] text-xs px-3 py-1 rounded-full flex items-center gap-1 font-bold shadow-md">
                      <Crown className="w-3.5 h-3.5" />Premium+
                    </span>
                  )}
                  {provider.plan_type === 'premium' && (
                    <span className="bg-[#ffff00] text-[#000000] text-xs px-3 py-1 rounded-full flex items-center gap-1 font-bold shadow-md">
                      Premium
                    </span>
                  )}
                  {provider.plan_type === 'destacado' && (
                    <span className="bg-[#000000] text-white text-xs px-3 py-1 rounded-full flex items-center gap-1">
                      <Star className="w-3 h-3" />Destacado
                    </span>
                  )}
                  {isClient && (
                    <button onClick={toggleFavorite} disabled={togglingFav} className="p-1 rounded-full hover:bg-white/40 transition-colors" data-testid="favorite-toggle-btn">
                      <Heart className={`w-6 h-6 transition-colors ${isFavorite ? 'fill-red-500 text-red-500' : 'text-[#000000] hover:text-red-400'}`} />
                    </button>
                  )}
                </div>
              </div>
              <h1 className="text-2xl font-bold text-[#000000] leading-tight mb-1" data-testid="provider-name">
                {provider.business_name}
                <EditBtn section="general" />
              </h1>
              {provider.verified && (
                <span className="inline-flex items-center gap-1 bg-[#ffff00] text-[#000000] text-xs font-bold px-2.5 py-1 rounded-full mb-1" data-testid="verified-badge">
                  <CheckCircle className="w-3.5 h-3.5" />
                  Resolución Sanitaria
                </span>
              )}
              {provider.address && <p className="text-sm text-[#000000]">{provider.address}</p>}
              <p className="text-sm font-bold text-[#000000]">{provider.municipio}</p>
            </div>

            {/* Desktop layout */}
            <div className="hidden md:flex items-end gap-4">
              <div className="w-24 h-24 rounded-2xl bg-white shadow-xl flex items-center justify-center overflow-hidden shrink-0">
                {provider.profile_photo ? (
                  <img src={getPhotoUrl(provider.profile_photo)} alt="" className="w-full h-full object-cover" />
                ) : provider.gallery?.[0]?.url ? (
                  <img src={getPhotoUrl(provider.gallery[0].url)} alt="" className="w-full h-full object-cover" />
                ) : provider.photos?.[0] ? (
                  <img src={provider.photos[0]} alt="" className="w-full h-full object-cover" />
                ) : (
                  <span className="text-4xl font-bold text-[#ffff00]">{provider.business_name?.[0]}</span>
                )}
              </div>
              <div className="pb-1 min-w-0">
                <div className="flex items-center gap-1 mb-1">
                  {[1,2,3,4,5].map(s => (
                    <Star key={s} className={`w-4 h-4 ${s <= Math.round(provider.rating || 0) ? 'fill-yellow-400 text-yellow-400' : 'text-gray-300'}`} />
                  ))}
                  <span className="text-sm font-bold text-[#000000] ml-1">{provider.rating?.toFixed(1) || '0.0'}</span>
                  <span className="text-sm text-[#000000]/60">({provider.total_reviews || 0} reseñas)</span>
                </div>
                <div className="flex flex-wrap items-center gap-2">
                  <h1 className="text-3xl font-bold text-[#000000]">{provider.business_name}</h1>
                  {provider.verified && (
                    <span className="inline-flex items-center gap-1 bg-[#ffff00] text-[#000000] text-xs font-bold px-2.5 py-1 rounded-full" data-testid="verified-badge-desktop">
                      <CheckCircle className="w-3.5 h-3.5" />
                      Resolución Sanitaria
                    </span>
                  )}
                  {provider.plan_type === 'premium_plus' && (
                    <span className="bg-gradient-to-r from-yellow-400 to-yellow-500 text-[#000000] text-xs px-2 py-0.5 rounded-full flex items-center gap-1 font-bold shadow-md">
                      <Crown className="w-3 h-3" />Premium+
                    </span>
                  )}
                  {provider.plan_type === 'premium' && (
                    <span className="bg-[#000000] text-white text-xs px-2 py-0.5 rounded-full flex items-center gap-1 font-bold shadow-md">
                      Premium
                    </span>
                  )}
                  {provider.plan_type === 'destacado' && (
                    <span className="bg-[#000000] text-white text-xs px-2 py-0.5 rounded-full flex items-center gap-1 shadow-md">
                      <Star className="w-3 h-3" />Destacado
                    </span>
                  )}
                  {isClient && (
                    <button onClick={toggleFavorite} disabled={togglingFav} className="p-1.5 rounded-full hover:bg-white/40 transition-colors" data-testid="favorite-toggle-btn">
                      <Heart className={`w-6 h-6 transition-colors ${isFavorite ? 'fill-red-500 text-red-500' : 'text-[#000000] hover:text-red-400'}`} />
                    </button>
                  )}
                  <EditBtn section="general" />
                </div>
                {provider.address && <p className="font-normal text-[#000000]">{provider.address}</p>}
                <p className="font-bold text-[#000000]">{provider.municipio}</p>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-5xl mx-auto px-4 py-8">
        <div className="grid lg:grid-cols-3 gap-8">
          {/* Main Content */}
          <div className="lg:col-span-2 space-y-6">

            {/* Premium Slider - Only for Premium+ providers */}
            {(provider.plan_type === 'premium_plus' && provider.premium_gallery?.length > 0) || isAdmin ? (
              <div className="relative">
                {isAdmin && (
                  <div className="flex justify-end mb-2">
                    <EditBtn section="premium_gallery" label="Gestionar Slider" />
                  </div>
                )}
                {provider.premium_gallery?.length > 0 && (
                  <PremiumSlider photos={provider.premium_gallery} />
                )}
                {isAdmin && (!provider.premium_gallery || provider.premium_gallery.length === 0) && (
                  <div className="bg-white rounded-2xl p-4 shadow-sm border-2 border-dashed border-gray-300 text-center">
                    <p className="text-sm text-gray-400">Sin fotos en slider premium</p>
                  </div>
                )}
              </div>
            ) : null}

            {/* Description - Sobre mi */}
            {provider.description && (
              <div className="bg-white rounded-2xl p-6 shadow-sm">
                <h2 className="text-xl font-bold mb-3">Sobre mi<EditBtn section="general" /></h2>
                <p className="text-gray-700 leading-relaxed">{provider.description}</p>
              </div>
            )}

            {/* YouTube Video - Only for Premium+ providers */}
            {provider.plan_type === 'premium_plus' && provider.youtube_video_url ? (
              <div className="relative w-full rounded-2xl overflow-hidden shadow-lg" data-testid="provider-youtube-video">
                {isAdmin && <div className="absolute top-2 right-2 z-10"><EditBtn section="youtube" /></div>}
                <div className="relative w-full aspect-[16/9]">
                  <iframe
                    src={`https://www.youtube.com/embed/${extractYouTubeId(provider.youtube_video_url)}`}
                    title="Video de la residencia"
                    className="absolute inset-0 w-full h-full"
                    frameBorder="0"
                    allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                    allowFullScreen
                  />
                </div>
              </div>
            ) : isAdmin ? (
              <div className="bg-white rounded-2xl p-4 shadow-sm border-2 border-dashed border-gray-300 text-center">
                <p className="text-sm text-gray-400 mb-2">Sin video YouTube</p>
                <EditBtn section="youtube" label="Agregar Video" />
              </div>
            ) : null}

            {/* Amenidades / Servicios */}
            <div className="relative">
              {isAdmin && <div className="absolute top-4 right-4 z-10"><EditBtn section="amenities" /></div>}
              <AmenitiesDisplay amenities={provider.amenities} />
              {isAdmin && (!provider.amenities || provider.amenities.length === 0) && (
                <div className="bg-white rounded-2xl p-4 shadow-sm border-2 border-dashed border-gray-300 text-center">
                  <p className="text-sm text-gray-400 mb-2">Sin servicios definidos</p>
                  <EditBtn section="amenities" label="Agregar Servicios" />
                </div>
              )}
            </div>

            {/* Photo Gallery - After services */}
            {(provider.gallery?.length > 0 || isAdmin) && (
              <div className="bg-white rounded-2xl p-6 shadow-sm" data-testid="provider-gallery-public">
                <h2 className="text-xl font-bold mb-4 flex items-center gap-2">
                  <span className="w-9 h-9 rounded-full bg-black flex items-center justify-center shrink-0">
                    <Camera className="w-5 h-5 text-[#ffff00]" />
                  </span>
                  Galeria
                  <EditBtn section="gallery" label="Gestionar Fotos" />
                </h2>
                <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                  {provider.gallery.map((photo, index) => (
                    <div 
                      key={photo.photo_id} 
                      className="aspect-[4/3] rounded-xl overflow-hidden bg-gray-100 cursor-pointer hover:opacity-90 transition-opacity"
                      onClick={() => window.open(getPhotoUrl(photo.url), '_blank')}
                    >
                      <img
                        src={getPhotoUrl(photo.thumbnail_url || photo.url)}
                        alt={`Foto ${index + 1}`}
                        className="w-full h-full object-cover"
                        loading="lazy"
                      />
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Personal Info (Más Datos) */}
            {((provider.personal_info && Object.values(provider.personal_info).some(v => v && v !== '' && v !== false && !(Array.isArray(v) && v.length === 0))) || isAdmin) && (
              <div className="bg-white rounded-2xl p-6 shadow-sm" data-testid="provider-personal-info">
                <h2 className="text-xl font-bold mb-4 flex items-center gap-2">
                  <span className="w-9 h-9 rounded-full bg-black flex items-center justify-center shrink-0">
                    <UserCircle className="w-5 h-5 text-[#ffff00]" />
                  </span>
                  Más Información
                  <EditBtn section="personal_info" />
                </h2>
                <div className="grid sm:grid-cols-2 gap-4">
                  {provider.services?.length > 0 && (
                    <div className="flex items-start gap-3 p-3 bg-gray-50 rounded-xl">
                      <span className="w-9 h-9 rounded-full bg-black flex items-center justify-center shrink-0">
                        <Home className="w-5 h-5 text-[#ffff00]" />
                      </span>
                      <div>
                        <p className="text-xs text-gray-500 font-medium">
                          Tipo de servicio
                        </p>
                        <p className="text-sm text-gray-800 capitalize">
                          {[...new Set(
                            provider.services
                              .map(s => s.service_type)
                              .filter(Boolean)
                          )].join(", ")}
                        </p>
                      </div>
                    </div>
                  )}
                  
                  {provider.personal_info?.daily_availability && (
                    <div className="flex items-start gap-3 p-3 bg-gray-50 rounded-xl">
                      <span className="w-9 h-9 rounded-full bg-black flex items-center justify-center shrink-0">
                        <Clock className="w-5 h-5 text-[#ffff00]" />
                      </span>
                      <div>
                        <p className="text-xs text-gray-500 font-medium">Horario de atención</p>
                        <p className="text-sm text-gray-800">{provider.personal_info.daily_availability}</p>
                      </div>
                    </div>
                  )}
                  {provider.personal_info?.bio && (
                    <div className="flex items-start gap-3 p-3 bg-gray-50 rounded-xl sm:col-span-2">
                      <span className="w-9 h-9 rounded-full bg-black flex items-center justify-center shrink-0">
                        <UserCircle className="w-5 h-5 text-[#ffff00]" />
                      </span>
                      <div>
                        <p className="text-xs text-gray-500 font-medium">Descripción adicional</p>
                        <p className="text-sm text-gray-800">{provider.personal_info.bio}</p>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* Service Zones - Municipios and Walking Zones */}
            {(provider.coverage_radius_km || provider.service_municipios?.length > 0 || provider.walking_zones?.length > 0) && (
              <div className="bg-white rounded-2xl p-6 shadow-sm" data-testid="provider-service-zones">
                <h2 className="text-xl font-bold mb-4 flex items-center gap-2">
                  <span className="w-9 h-9 rounded-full bg-black flex items-center justify-center shrink-0">
                    <MapPin className="w-5 h-5 text-[#ffff00]" />
                  </span>
                  Zona de Servicio
                </h2>
                
                {provider.coverage_radius_km && (
                  <div className="mb-4 p-3 bg-cyan-50 rounded-xl">
                    <p className="text-sm text-blue-800">
                      <strong>Radio de cobertura:</strong> {provider.coverage_radius_km} km desde su ubicación
                    </p>
                  </div>
                )}
                
                {provider.service_municipios?.length > 0 && (
                  <div className="mb-4">
                    <h3 className="text-sm font-medium text-gray-600 mb-2">Municipios donde ofrece servicio:</h3>
                    <div className="flex flex-wrap gap-2">
                      {provider.service_municipios.map(municipio => (
                        <span 
                          key={municipio}
                          className="px-3 py-1.5 bg-gray-100 text-gray-800 rounded-full text-sm"
                        >
                          {municipio}
                        </span>
                      ))}
                    </div>
                  </div>
                )}
                
                {provider.walking_zones?.length > 0 && (
                  <div>
                    <h3 className="text-sm font-medium text-gray-600 mb-2">Zonas de cobertura:</h3>
                    <div className="flex flex-wrap gap-2">
                      {provider.walking_zones.map(zone => (
                        <span 
                          key={zone}
                          className="px-3 py-1.5 bg-green-100 text-green-800 rounded-full text-sm"
                        >
                          {zone}
                        </span>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            )}

            {/* Reviews */}
            <div className="bg-white rounded-2xl p-6 shadow-sm">
              <h2 className="text-xl font-bold mb-4">Reseñas</h2>

              {/* Write Review - Formulario con 5 criterios */}
              {user ? (
                <div className="mb-6 p-6 bg-gray-50 rounded-xl" data-testid="review-form">
                  <h3 className="text-lg font-bold text-[#000000] mb-4">Deja tu reseña</h3>
                  
                  {/* 5 Criterios de evaluación */}
                  <div className="space-y-4 mb-6">
                    {REVIEW_CRITERIA.map((criterion) => (
                      <div key={criterion.id} className="flex items-center justify-between py-2 border-b border-gray-200">
                        <span className="text-base text-[#000000] font-medium">{criterion.label}</span>
                        <StarRating
                          value={reviewCriteria[criterion.id]}
                          onChange={(value) => setReviewCriteria(prev => ({ ...prev, [criterion.id]: value }))}
                        />
                      </div>
                    ))}
                  </div>

                  {/* Promedio calculado */}
                  {allCriteriaFilled() && (
                    <div className="mb-4 p-3 bg-[#ffff00]/10 rounded-lg text-center">
                      <span className="text-[#000000] font-semibold">
                        Puntuación promedio: {calculateAverageRating().toFixed(1)} ⭐
                      </span>
                    </div>
                  )}

                  {/* Comentario */}
                  <textarea
                    value={reviewText}
                    onChange={e => setReviewText(e.target.value)}
                    placeholder="Ingresa tu comentario..."
                    className="w-full border-2 border-gray-200 rounded-xl p-4 text-base min-h-[120px] focus:outline-none focus:ring-2 focus:ring-[#ffff00] focus:border-[#ffff00] mb-4 text-[#000000] placeholder-gray-400"
                    data-testid="review-text-input"
                  />

                  {/* Photo Upload */}
                  <div className="flex flex-wrap gap-2 mb-4">
                    {reviewPhotos.map((photo, i) => {
                      const imageSrc =
                        typeof photo === 'string'
                          ? (
                              photo.startsWith('http') ||
                              photo.startsWith('blob:')
                                ? photo
                                : `${process.env.REACT_APP_BACKEND_URL}${photo}`
                            )
                          : (
                              photo.preview ||
                              (
                                photo.url?.startsWith('http')
                                  ? photo.url
                                  : `${process.env.REACT_APP_BACKEND_URL}${photo.url}`
                              )
                            );

                      return (
                        <div key={i} className="relative w-20 h-20 rounded-lg overflow-hidden border">
                          <img
                            src={imageSrc}
                            alt={`Foto de reseña ${i + 1}`}
                            className="w-full h-full object-cover"
                          />
                          <button
                            type="button"
                            onClick={() => {
                              setReviewPhotos(prev => {
                                const selected = prev[i];

                                if (
                                  typeof selected === 'object' &&
                                  selected?.preview?.startsWith('blob:')
                                ) {
                                  URL.revokeObjectURL(selected.preview);
                                }

                                return prev.filter((_, j) => j !== i);
                              });
                            }}
                            className="absolute top-0.5 right-0.5 bg-black/60 text-white rounded-full p-0.5"
                          >
                            <X className="w-3 h-3" />
                          </button>
                        </div>
                      );
                    })}
                    {reviewPhotos.length < 4 && (
                      <button
                        onClick={() => fileInputRef.current?.click()}
                        disabled={uploading}
                        className="w-20 h-20 rounded-lg border-2 border-dashed border-gray-300 flex flex-col items-center justify-center text-gray-400 hover:border-[#ffff00] hover:text-[#ffff00] transition-colors"
                        data-testid="upload-photo-button"
                      >
                        <Camera className="w-5 h-5" />
                        <span className="text-xs mt-1">{uploading ? 'Subiendo...' : 'Añadir foto'}</span>
                      </button>
                    )}
                    <input ref={fileInputRef} type="file" accept="image/*" multiple className="hidden" onChange={handlePhotoUpload} />
                  </div>

                  {/* Mensaje de validación */}
                  {!canSubmitReview() && (
                    <p className="text-sm text-gray-500 mb-3">
                      * Debes evaluar todos los criterios y escribir un comentario para enviar tu reseña
                    </p>
                  )}

                  {/* Botón de envío */}
                  <Button
                    onClick={submitReview}
                    disabled={submitting || !canSubmitReview()}
                    className={`w-full py-4 text-lg font-bold ${canSubmitReview() ? 'bg-[#ffff00] hover:bg-[#e6e600] text-[#000000]' : 'bg-gray-300 text-gray-500 cursor-not-allowed'}`}
                    data-testid="submit-review-button"
                  >
                    {submitting ? 'Enviando...' : 'Publicar Reseña'}
                  </Button>
                </div>
              ) : (
                <div className="mb-6 p-5 bg-gray-50 rounded-xl text-center" data-testid="review-login-prompt">
                  <p className="text-[#000000] font-medium mb-2">Para dejar una reseña debes iniciar sesión</p>
                  <Link to={`/login?returnTo=${encodeURIComponent(window.location.pathname + window.location.search)}`} className="font-bold text-black hover:text-[#d6b800] hover:underline transition-colors">Iniciar Sesión</Link>
                </div>
              )}

              {/* Review List */}
              {(() => {
                const platformReviews = (provider.reviews || []).map(r => ({ ...r, source: 'platform' }));
                const googleReviews = (provider.google_reviews || []).map(r => ({
                  user_name: r.author,
                  user_picture: r.author_photo,
                  rating: r.rating,
                  overall_rating: r.rating,
                  comment: r.text,
                  time_description: r.time_description,
                  source: 'google',
                }));
                const allReviews = [...platformReviews, ...googleReviews];
                return allReviews.length > 0 ? (
                <div className="space-y-4">
                  {allReviews.map((r, i) => (
                    <div key={i} className="border-b last:border-0 pb-4 last:pb-0">
                      <div className="flex items-center gap-2 mb-2">
                        <div className="w-8 h-8 rounded-full bg-gray-200 overflow-hidden flex items-center justify-center">
                          {r.user_picture ? <img src={r.user_picture} alt="" className="w-full h-full object-cover" referrerPolicy="no-referrer" /> : <span className="text-sm font-bold">{r.user_name?.[0]}</span>}
                        </div>
                        <span className="font-medium text-sm">{r.user_name}</span>
                        <div className="flex gap-0.5">
                          {[1,2,3,4,5].map(s => <Star key={s} className={`w-3.5 h-3.5 ${s <= (r.rating || r.overall_rating) ? 'fill-yellow-400 text-yellow-400' : 'text-gray-300'}`} />)}
                        </div>
                        {r.time_description && <span className="text-xs text-gray-400 ml-auto">{r.time_description}</span>}
                      </div>
                      {r.comment && <p className="text-sm text-gray-700">{r.comment}</p>}
                      {r.photos?.length > 0 && (
                        <div className="flex gap-2 mt-2">
                          {r.photos.map((url, j) => (
                            <img key={j} src={url.startsWith('http') ? url : `${process.env.REACT_APP_BACKEND_URL}${url}`} alt="" className="w-20 h-20 rounded-lg object-cover border" />
                          ))}
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-gray-400 text-center py-4">Sin reseñas aún</p>
              );
              })()}
            </div>
          </div>

          {/* Sidebar */}
          <div className="space-y-6">
            {/* Tipo de Cuidado */}
            {false && (
              <div className="bg-white rounded-2xl p-5 shadow-sm" data-testid="provider-care-types">
                <div className="flex items-center justify-between mb-3">
                  <h3 className="font-bold text-base text-[#000000]">Tipo de Valencia</h3>
                  {isAdmin && <EditBtn section="care_types" />}
                </div>
                {provider.care_types && provider.care_types.length > 0 ? (
                  <div className="flex flex-wrap gap-2">
                    {provider.care_types.map((ct, i) => (
                      <span key={i} className="px-3 py-1.5 bg-[#ffff00]/20 text-[#000000] text-sm font-semibold rounded-full border border-[#ffff00]/40" data-testid={`care-type-badge-${ct.toLowerCase()}`}>
                        {ct}
                      </span>
                    ))}
                  </div>
                ) : (
                  <p className="text-sm text-gray-400">Sin tipos de valencia definidos</p>
                )}
              </div>
            )}

            {/* Redes Sociales */}
            {((provider.social_links && (provider.social_links.instagram || provider.social_links.facebook || provider.social_links.website)) || isAdmin) && (
              <div className="bg-white rounded-2xl p-5 shadow-sm" data-testid="provider-social-links">
                {isAdmin && <div className="text-center mb-2"><EditBtn section="social" /></div>}
                <div className="flex items-center justify-center gap-5">
                  {provider.social_links?.instagram && (
                    <a href={provider.social_links.instagram} target="_blank" rel="noopener noreferrer" 
                       className="group w-11 h-11 rounded-full bg-[#000000] flex items-center justify-center hover:bg-[#000000] transition-colors"
                       data-testid="social-instagram">
                      <Instagram className="w-5 h-5 text-white group-hover:text-[#ffff00] transition-colors" />
                    </a>
                  )}
                  {provider.social_links?.facebook && (
                    <a href={provider.social_links.facebook} target="_blank" rel="noopener noreferrer"
                       className="group w-11 h-11 rounded-full bg-[#000000] flex items-center justify-center hover:bg-[#000000] transition-colors"
                       data-testid="social-facebook">
                      <Facebook className="w-5 h-5 text-white group-hover:text-[#ffff00] transition-colors" />
                    </a>
                  )}
                  {provider.social_links?.website && (
                    <a href={provider.social_links.website} target="_blank" rel="noopener noreferrer"
                       className="group w-11 h-11 rounded-full bg-[#000000] flex items-center justify-center hover:bg-[#000000] transition-colors"
                       data-testid="social-website">
                      <Globe className="w-5 h-5 text-white group-hover:text-[#ffff00] transition-colors" />
                    </a>
                  )}
                </div>
              </div>
            )}

            {/* Precio */}
            {((provider.services?.filter(s => s.price_from > 0 || s.description).length > 0) || isAdmin) && (
              <div className="bg-white rounded-2xl p-6 shadow-sm">
                <h3 className="font-bold text-lg mb-4">Precio<EditBtn section="services" /></h3>
                {provider.services.filter(s => s.price_from > 0 || s.description).map((s, i) => {
                  const formatServiceName = (type) => {
                    const names = {
                      'residencias': 'Residencias',
                      'cuidado-domicilio': 'Cuidado a Domicilio',
                      'salud-mental': 'Salud Mental'
                    };
                    return names[type] || type;
                  };
                  const hasSubPrices = s.sub_prices && s.sub_prices.length > 0;
                  const isExpanded = expandedServices[i];
                  return (
                    <div key={i} className="mb-3 last:mb-0">
                      <div
                        className={`p-3 bg-gray-50 rounded-xl ${hasSubPrices ? 'cursor-pointer hover:bg-gray-100 transition-colors' : ''}`}
                        onClick={() => hasSubPrices && setExpandedServices(prev => ({ ...prev, [i]: !prev[i] }))}
                        data-testid={`service-card-${i}`}
                      >
                        <div className="flex items-center justify-between">
                          <div>
                            <span className="font-semibold text-[#000000] text-sm">{formatServiceName(s.service_type)}</span>
                            {s.description && <p className="text-xs text-gray-500 mt-0.5">{s.description}</p>}
                            <span className="text-[#000000] font-bold text-lg block mt-1">Desde {s.price_from?.toLocaleString('es-ES')} €</span>
                          </div>
                          {hasSubPrices && (
                            <div className="flex items-center gap-1 text-xs text-gray-400">
                              <span>{s.sub_prices.length} detalle{s.sub_prices.length > 1 ? 's' : ''}</span>
                              {isExpanded ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
                            </div>
                          )}
                        </div>
                      </div>
                      {hasSubPrices && isExpanded && (
                        <div className="ml-4 mt-1 space-y-1 border-l-2 border-[#ffff00]/30 pl-3" data-testid={`sub-prices-${i}`}>
                          {s.sub_prices.map((sp, j) => (
                            <div key={j} className="flex items-center justify-between py-1.5 px-2 bg-white rounded-lg text-sm" data-testid={`sub-price-${i}-${j}`}>
                              <span className="text-gray-600">{sp.name}</span>
                              <span className="font-bold text-[#000000]">{(sp.price || 0).toLocaleString('es-ES')} €</span>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            )}

            {/* Tipo de Estadía */}
            {false && (
              <div className="bg-white rounded-2xl p-5 shadow-sm" data-testid="provider-stay-types">
                <div className="flex items-center justify-between mb-3">
                  <h3 className="font-bold text-base text-[#000000]">Tipo de Estadía</h3>
                  {isAdmin && <EditBtn section="stay_types" />}
                </div>
                {provider.stay_types && provider.stay_types.length > 0 ? (
                  <div className="flex flex-wrap gap-2">
                    {provider.stay_types.map((st, i) => (
                      <span key={i} className="px-3 py-1.5 bg-[#ffff00]/20 text-[#000000] text-sm font-semibold rounded-full border border-[#ffff00]/40" data-testid={`stay-type-badge-${i}`}>
                        {st}
                      </span>
                    ))}
                  </div>
                ) : (
                  <p className="text-sm text-gray-400">Sin tipos de estadía definidos</p>
                )}
              </div>
            )}

            {/* Contacto */}
            <div className="bg-white rounded-2xl p-6 shadow-sm">
              <h3 className="font-bold text-lg mb-4">Contacto<EditBtn section="contact" /></h3>

              {/* Connected: Full contact visible */}
              {provider.viewer_is_connected ? (
                <div className="space-y-3" data-testid="contact-info-connected">
                  <div className="p-3 bg-green-50 border border-green-200 rounded-xl text-center mb-3">
                    <CheckCircle className="w-5 h-5 text-green-600 mx-auto mb-1" />
                    <p className="text-sm font-medium text-green-700">Conectados</p>
                    <p className="text-xs text-green-600">Ya puedes chatear y coordinar</p>
                  </div>

                  <Link to="/chat" className="block">
                    <Button className="w-full bg-[#ffff00] hover:bg-[#e6e600] text-[#000000] py-5 text-base" data-testid="go-to-chat-btn">
                      <MessageSquare className="w-5 h-5 mr-2" /> Ir al Chat
                    </Button>
                  </Link>

                  <div className="pt-3 border-t space-y-3">
                    {provider.address && (
                      <div className="flex items-start gap-3">
                        <span className="w-9 h-9 rounded-full bg-black flex items-center justify-center shrink-0">
                          <MapPin className="w-5 h-5 text-[#ffff00]" />
                        </span>
                        <span className="text-sm text-gray-700">{provider.address}</span>
                      </div>
                    )}
                    {provider.phone && (['premium', 'premium_plus'].includes(provider.plan_type) || isAdmin) && (
                      <a href={`tel:${provider.phone}`} className="flex items-center gap-3 p-3 bg-gray-50 rounded-xl hover:bg-gray-100 transition-colors">
                        <span className="w-9 h-9 rounded-full bg-black flex items-center justify-center shrink-0">
                          <Phone className="w-5 h-5 text-[#ffff00]" />
                        </span>
                        <span className="text-sm font-medium">{provider.phone}</span>
                      </a>
                    )}
                    {provider.phone && !['premium', 'premium_plus'].includes(provider.plan_type) && !isAdmin && (
                      <div className="flex items-center gap-3 p-3 bg-gray-50 rounded-xl opacity-60">
                        <Lock className="w-5 h-5 text-gray-400" />
                        <span className="text-sm text-gray-400">Telefono disponible solo para premium</span>
                      </div>
                    )}
                    {provider.whatsapp && (['premium', 'premium_plus'].includes(provider.plan_type) || isAdmin) && (
                      <a href={`https://wa.me/${provider.whatsapp}`} target="_blank" rel="noreferrer" className="flex items-center gap-3 p-3 bg-green-50 rounded-xl hover:bg-green-100 transition-colors">
                        <MessageSquare className="w-5 h-5 text-green-600" />
                        <span className="text-sm font-medium text-green-700">WhatsApp</span>
                      </a>
                    )}
                    {provider.whatsapp && !['premium', 'premium_plus'].includes(provider.plan_type) && !isAdmin && (
                      <div className="flex items-center gap-3 p-3 bg-gray-50 rounded-xl opacity-60">
                        <Lock className="w-5 h-5 text-gray-400" />
                        <span className="text-sm text-gray-400">WhatsApp disponible solo para premium</span>
                      </div>
                    )}
                  </div>
                </div>

              ) : provider.viewer_has_pending_request ? (
                /* Pending request */
                <div className="text-center space-y-3" data-testid="contact-pending">
                  <div className="w-16 h-16 bg-yellow-50 rounded-full flex items-center justify-center mx-auto mb-3">
                    <Loader2 className="w-8 h-8 text-yellow-500 animate-spin" />
                  </div>
                  <p className="text-sm font-medium text-yellow-700">Solicitud enviada</p>
                  <p className="text-xs text-gray-500">
                    Esperando que el servicio acepte tu solicitud de contacto. Te notificaremos cuando responda.
                  </p>
                </div>

              ) : user ? (
                /* Cliente logueado: puede enviar solicitud de contacto */
                <div className="space-y-3" data-testid="contact-request-form">
                  <p className="text-sm text-gray-600 mb-2">
                    Envía una solicitud de contacto al servicio. Si cuenta con Premium activo, podrá responderte directamente por chat. Si no, nuestro equipo gestionará el contacto.
                  </p>
                  <textarea
                    value={contactSent ? 'Mensaje enviado' : contactMessage}
                    onChange={e => setContactMessage(e.target.value)}
                    placeholder="Hola, me gustaría contactarte para un servicio..."
                    disabled={contactSent}
                    className={`w-full border-2 rounded-xl p-4 text-base min-h-[100px] focus:outline-none focus:ring-2 focus:ring-[#ffff00] ${
                      contactSent
                        ? 'border-green-200 bg-green-50 text-green-700 font-medium'
                        : 'border-gray-200'
                    }`}
                    data-testid="contact-message-input"
                  />
                  <Button
                    onClick={async () => {
                      setSendingContactRequest(true);
                      try {
                        await api.post('/contact-requests', {
                          provider_user_id: provider.user_id,
                          message: contactMessage || 'Hola, me gustaría contactarte para un servicio.'
                        });
                        setContactMessage('');
                        setContactSent(true);
                        toast.success('Solicitud enviada. El equipo de PetAdvisor gestionará tu contacto.');
                        loadProvider();
                      } catch (e) {
                        toast.error(e.response?.data?.detail || 'Error al enviar solicitud');
                      } finally {
                        setSendingContactRequest(false);
                      }
                    }}
                    disabled={sendingContactRequest || contactSent}
                    className="w-full bg-[#ffff00] hover:bg-[#e6e600] text-[#000000] py-5 text-lg font-bold"
                    data-testid="send-contact-request-btn"
                  >
                    {sendingContactRequest ? <Loader2 className="w-5 h-5 animate-spin mr-2" /> : <Send className="w-5 h-5 mr-2" />}
                    {contactSent ? 'Mensaje enviado' : 'Solicitar Contacto'}
                  </Button>
                </div>

              ) : (
                /* No logueado: mostrar botón de login */
                <div className="space-y-4" data-testid="contact-login-required">
                  <div className="text-center">
                    <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-3">
                      <Lock className="w-8 h-8 text-gray-400" />
                    </div>
                    <p className="text-base text-gray-600 mb-4">
                      Inicia sesión para contactar a este servicio
                    </p>
                    <Link to={`/login?returnTo=${encodeURIComponent(window.location.pathname + window.location.search)}`}>
                      <Button className="w-full bg-[#ffff00] hover:bg-[#e6e600] text-[#000000] py-4 text-lg font-bold">
                        Iniciar Sesión
                      </Button>
                    </Link>
                    <p className="text-sm text-gray-500 mt-3">
                      ¿No tienes cuenta? <Link to={`/register?returnTo=${encodeURIComponent(window.location.pathname + window.location.search)}`} className="font-bold text-black hover:text-[#d6b800] hover:underline transition-colors">Regístrate gratis</Link>
                    </p>
                  </div>
                </div>
              )}
            </div>

            {/* Location Map */}
            {(provider.latitude || provider.address || provider.municipio) && (
              <div className="bg-white rounded-2xl shadow-sm overflow-hidden" data-testid="provider-map">
                <div className="p-4 border-b">
                  <h3 className="font-bold text-lg flex items-center gap-2">
                    <span className="w-9 h-9 rounded-full bg-black flex items-center justify-center shrink-0">
                      <MapPin className="w-5 h-5 text-[#ffff00]" />
                    </span>
                    Ubicación
                  </h3>
                </div>
                <SafeMap latitude={provider.latitude} longitude={provider.longitude} address={provider.address} municipio={provider.municipio} />
              </div>
            )}

            {/* CTA para dueños */}
            <div className="bg-[#000000] rounded-2xl p-6 text-center" data-testid="owner-cta">
              <h3 className="font-bold text-white text-lg mb-2">¿Administras este servicio?</h3>
              <p className="text-white/70 text-sm mb-4">
                Si administras este servicio y deseas completar o actualizar la información de tu perfil, contáctanos y te ayudamos.
              </p>
              <button
                onClick={() => setShowOwnerContact(true)}
                className="inline-block w-full py-3 bg-[#ffff00] hover:bg-[#e6e600] text-[#000000] font-bold rounded-xl transition-colors text-center"
                data-testid="owner-cta-btn"
              >
                Contáctanos
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Booking Form Modal */}
      {showBookingForm && (
        <BookingForm 
          provider={provider} 
          onClose={() => setShowBookingForm(false)}
          onSuccess={() => {
            setShowBookingForm(false);
            toast.success('Reserva enviada correctamente');
          }}
        />
      )}

      {/* Owner Contact Modal */}
      {showOwnerContact && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 px-4" onClick={() => setShowOwnerContact(false)}>
          <div className="bg-white rounded-2xl w-full max-w-md p-6 relative" onClick={e => e.stopPropagation()} data-testid="owner-contact-modal">
            <button onClick={() => setShowOwnerContact(false)} className="absolute top-4 right-4 text-gray-400 hover:text-gray-600">
              <X className="w-5 h-5" />
            </button>
            <h3 className="text-xl font-bold text-[#000000] mb-1">Contáctanos</h3>
            <p className="text-sm text-gray-400 mb-5">Completa el formulario y te contactaremos</p>
            <form onSubmit={async (e) => {
              e.preventDefault();
              setSendingOwnerForm(true);
              try {
                await api.post('/providers/owner-contact', {
                  nombre: ownerForm.nombre,
                  email: ownerForm.email,
                  asunto: provider?.business_name || 'Residencia',
                  mensaje: ownerForm.mensaje
                });
                toast.success('Mensaje enviado correctamente');
                setShowOwnerContact(false);
                setOwnerForm({ nombre: '', email: '', mensaje: '' });
              } catch {
                toast.error('Error al enviar el mensaje');
              } finally {
                setSendingOwnerForm(false);
              }
            }} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-[#000000] mb-1">Nombre</label>
                <input type="text" required value={ownerForm.nombre} onChange={e => setOwnerForm(p => ({...p, nombre: e.target.value}))} className="w-full border border-gray-200 rounded-xl px-4 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-[#ffff00]" placeholder="Tu nombre" data-testid="owner-contact-name" />
              </div>
              <div>
                <label className="block text-sm font-medium text-[#000000] mb-1">Email</label>
                <input type="email" required value={ownerForm.email} onChange={e => setOwnerForm(p => ({...p, email: e.target.value}))} className="w-full border border-gray-200 rounded-xl px-4 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-[#ffff00]" placeholder="tu@email.com" data-testid="owner-contact-email" />
              </div>
              <div>
                <label className="block text-sm font-medium text-[#000000] mb-1">Asunto</label>
                <input type="text" readOnly value={provider?.business_name || ''} className="w-full border border-gray-200 rounded-xl px-4 py-2.5 text-sm bg-gray-50 text-gray-500" data-testid="owner-contact-subject" />
              </div>
              <div>
                <label className="block text-sm font-medium text-[#000000] mb-1">Mensaje</label>
                <textarea required rows={4} value={ownerForm.mensaje} onChange={e => setOwnerForm(p => ({...p, mensaje: e.target.value}))} className="w-full border border-gray-200 rounded-xl px-4 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-[#ffff00] resize-none" placeholder="Escribe lo que necesitas..." data-testid="owner-contact-message" />
              </div>
              <button type="submit" disabled={sendingOwnerForm} className="w-full py-3 bg-[#ffff00] hover:bg-[#f2f200] text-[#000000] font-bold rounded-xl transition-colors disabled:opacity-50 flex items-center justify-center gap-2" data-testid="owner-contact-submit">
                {sendingOwnerForm ? <><Loader2 className="w-4 h-4 animate-spin" /> Enviando...</> : 'Enviar'}
              </button>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
